import { BTFileSystem } from "legacyComponents/FileUploadContainer.types";

import { EmptyResponseEntity } from "types/emptyResponseEntity";
import { DocumentInstanceType, type MediaType } from "types/enum";

import { apixHandler } from "utilities/api/apix-handler";
import { APIHandler } from "utilities/apiHandler";

import {
    type AttachVideoRequest,
    BTFileUploadDocsRequest,
    BTFileUploadEntity,
    BTFileUploadPhotosRequest,
    BTFileUploadPhotosResponse,
    type CreateVideoRequest,
    type EntityDocsRequest,
    EntityDocsResponse,
    type IBTFileUploadFormValues,
} from "commonComponents/entity/media/BTFileUpload/BTFileUpload.api.types";

import { type AttachVideoByShareTokenResponse } from "handlers";

export interface IBTFileUploadHandler {
    get(folderId: number, jobId: number, mediaType: MediaType): Promise<BTFileUploadEntity>;
    saveDocs(folderId: number, values: IBTFileUploadFormValues): Promise<EmptyResponseEntity>;
    savePhotos(
        folderId: number,
        values: IBTFileUploadFormValues,
    ): Promise<BTFileUploadPhotosResponse>;
    saveVideo(data: CreateVideoRequest): Promise<EmptyResponseEntity>;
    attachVideo(data: AttachVideoRequest): Promise<BTFileSystem>;
    attachVideoExternal(
        data: AttachVideoRequest,
        subId: string,
        entityId: string,
    ): Promise<BTFileSystem>;
    attachVideoByShareToken(
        data: AttachVideoRequest,
        shareToken: string,
        docType: DocumentInstanceType,
    ): Promise<BTFileSystem>;
    saveEntityDocs(data: EntityDocsRequest): Promise<EntityDocsResponse>;
    postEntityDocsExternal(
        data: EntityDocsRequest,
        subId: string,
        entityId: string,
        docType: DocumentInstanceType,
        shareToken?: string,
    ): Promise<EntityDocsResponse>;
}

export class FileUploadHandler implements IBTFileUploadHandler {
    async get(folderId: number, jobId: number, mediaType: MediaType): Promise<BTFileUploadEntity> {
        return await APIHandler("/api/Media/FileUploader", {
            method: "GET",
            data: { folderId, jobId, mediaType },
            responseType: BTFileUploadEntity,
        });
    }

    async saveDocs(
        folderId: number,
        values: IBTFileUploadFormValues,
    ): Promise<EmptyResponseEntity> {
        return await APIHandler("/api/Documents/TempDocuments", {
            method: "POST",
            data: new BTFileUploadDocsRequest(folderId, values),
            responseType: EmptyResponseEntity,
        });
    }

    async savePhotos(
        folderId: number,
        values: IBTFileUploadFormValues,
    ): Promise<BTFileUploadPhotosResponse> {
        return await APIHandler("/api/Photos/SaveTempPhotos", {
            method: "POST",
            data: new BTFileUploadPhotosRequest(folderId, values),
            responseType: BTFileUploadPhotosResponse,
        });
    }

    async saveVideo(data: CreateVideoRequest): Promise<EmptyResponseEntity> {
        return await APIHandler("/api/Videos/CreateVideo", {
            method: "POST",
            data,
            responseType: EmptyResponseEntity,
        });
    }

    async attachVideo(data: AttachVideoRequest): Promise<BTFileSystem> {
        return await APIHandler(`/api/Videos/Attach?jobId=${data.jobId}`, {
            method: "PUT",
            data,
            responseType: BTFileSystem,
        });
    }

    async saveEntityDocs(data: EntityDocsRequest): Promise<EntityDocsResponse> {
        return await APIHandler("/api/Documents/EntityDocs", {
            method: "POST",
            data,
            responseType: EntityDocsResponse,
        });
    }

    // External Uploads
    async attachVideoExternal(
        data: AttachVideoRequest,
        subId: string,
        entityId: string,
    ): Promise<BTFileSystem> {
        return await APIHandler(
            `/api/Videos/AttachExternal?jobId=${data.jobId}&entityId=${entityId}&subId=${subId}`,
            {
                method: "PUT",
                data,
                responseType: BTFileSystem,
            },
        );
    }

    async attachVideoByShareToken(
        data: AttachVideoRequest,
        shareToken: string,
        docType: DocumentInstanceType,
    ): Promise<BTFileSystem> {
        const body = {
            videoId: data.videoId,
            videoName: data.videoName,
            videoDescription: data.videoDescription,
            fileSize: data.fileSize,
            showSubs: data.showSubs,
            showOwner: data.showOwner,
            notifyBuilder: data.notifyBuilder,
            notifyOwner: data.notifyOwner,
            notifySubs: data.notifySubs,
        };

        switch (docType) {
            case DocumentInstanceType.Submittal: {
                const resp = await apixHandler({
                    url: `/apix/v2/external/${shareToken}/submittals/videos`,
                    method: "POST",
                    headers: { "Content-Type": "application/merge-patch+json" },
                    data: body,
                });
                return new BTFileSystem(
                    (resp.data as AttachVideoByShareTokenResponse).attachedFile!,
                );
            }
            default:
                throw new Error(
                    `attachVideoByShareToken is not supported for document type ${docType}`,
                );
        }
    }

    // External Uploads
    async postEntityDocsExternal(
        data: EntityDocsRequest,
        subId: string,
        entityId: string,
        docType: DocumentInstanceType,
        shareToken?: string,
    ): Promise<EntityDocsResponse> {
        switch (docType) {
            case DocumentInstanceType.Submittal:
                if (!shareToken) {
                    throw new Error("shareToken is required for Submittal document type");
                }
                const resp = await apixHandler({
                    url: `/apix/v2/external/${shareToken}/submittals/attachments`,
                    method: "POST",
                    headers: { "Content-Type": "application/merge-patch+json" },
                    data,
                });
                return new EntityDocsResponse(resp.data);
            default:
                return await APIHandler(
                    `/api/documents/entityDocsExternal?docType=${docType}&entityId=${entityId}&subId=${subId}`,
                    {
                        method: "POST",
                        data,
                        responseType: EntityDocsResponse,
                    },
                );
        }
    }
}
